import { useState, useRef, useEffect, useCallback } from "react";
import { ArrowLeft, Play, Pause, Loader2, Copy, Plus, Download, ChevronLeft, ChevronRight, Mic2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CDN = "https://static.prod-images.emergentagent.com/jobs/34da0eef-e77f-49cc-8e45-86973d361acf/images";

const HERO_SLIDES = [
  {
    img: `${CDN}/68edf34f9d66d94823632994b5ca6373fda610d94fa5a59f5ae647d5878abf3f.jpeg`,
    kicker: "MANNIS VOICES", title: "SING BETTER.",
    sub: "175+ production-ready style reference samples for all AI music platforms — preview, copy the style prompt, and drop them straight into your workflow.",
    cta: "BROWSE ALL COLLECTIONS", action: "all"
  },
  {
    img: `${CDN}/d6668a8d2810cfd8601e975106e0e67056dbb7d94786574c978afbf12e71d0b7.jpeg`,
    kicker: "SIGNATURE PACKS", title: "ATL TRAP & HIP-HOP",
    sub: "Street-certified vocal styles — melodic trap, drill and boom-bap voices tuned for 808s.",
    cta: "EXPLORE HIP-HOP", action: "hiphop"
  },
  {
    img: `${CDN}/e58d17595c52f5b6dbc8ffd867f7a922898185611ad99841596f13544a23c9a7.jpeg`,
    kicker: "WORLD VOICE DNA", title: "VOICES FROM EVERY CORNER OF THE WORLD",
    sub: "K-Pop, Afrobeats, Latin, Reggae, Caribbean and more — authentic regional character in every take.",
    cta: "EXPLORE WORLD VOICES", action: "world"
  },
];

const FAMILIES = [
  {
    key: "popsoul", label: "POP & SOUL", accent: "#f0b429",
    img: `${CDN}/c31611b6b1379ad29c4da634432fb26d4e34d5503ff2d21e455110314d9bc077.jpeg`,
    desc: "Polished pop leads, velvet R&B, Motown soul, gospel power, blues grit and jazz intimacy. Chart-ready vocal styles.",
    packs: ["pop", "rnb", "divas", "gospel", "jazz", "soul", "blues"]
  },
  {
    key: "hiphop", label: "HIP-HOP & TRAP", accent: "#ef4444",
    img: `${CDN}/38185390c48dc23823da348a0cc10ac59f57e4fda1a944764096b64d1a0ba8ab.jpeg`,
    desc: "ATL trap melodies, NYC boom-bap grit, UK & NY drill energy. Street-certified voices built for 808s.",
    packs: ["hiphop", "trap", "atl", "nyc", "drill"]
  },
  {
    key: "world", label: "WORLD VOICES", accent: "#22d3ee",
    img: `${CDN}/ac2fd3154d8acb404efff2f2f2737319a3f66d41957885182858f6475f840569.jpeg`,
    desc: "K-Pop, J-Pop, Bollywood, Arabic pop, Afrobeats, Latin, Brazilian, reggae and voices from every continent.",
    packs: ["latin", "afrobeats", "kpop", "reggae", "caribbean", "asia", "europe", "africa", "bollywood", "arabic", "brazil", "jpop"]
  },
  {
    key: "stage", label: "STAGE & ELECTRIC", accent: "#a855f7",
    img: `${CDN}/d36536a232fd8469559ea766f173d4c6e86cd6da746ec6b0341eed3357d9a998.jpeg`,
    desc: "Arena rock and metal power, country heart, EDM toplines, lo-fi moods, hyperpop chaos and theater drama.",
    packs: ["rock", "country", "electronic", "indie", "musical", "metal", "lofi", "hyperpop", "opera"]
  },
];

const familyOfPack = (packKey) => FAMILIES.find(f => f.packs.includes(packKey));

function HeroCarousel({ onAction }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % HERO_SLIDES.length), 6000);
    return () => clearInterval(t);
  }, []);
  const s = HERO_SLIDES[idx];
  return (
    <div data-testid="voices-hero" style={{
      position: "relative", borderRadius: "18px", overflow: "hidden", marginBottom: "28px",
      border: "1px solid rgba(255,255,255,0.1)", minHeight: "340px",
      background: `linear-gradient(90deg, rgba(5,8,16,0.92) 0%, rgba(5,8,16,0.55) 55%, rgba(5,8,16,0.15) 100%), url(${s.img}) center/cover no-repeat`,
      transition: "background-image 0.6s ease", display: "flex", alignItems: "center"
    }}>
      <div style={{ padding: "40px 48px", maxWidth: "620px" }}>
        <span style={{ fontSize: "0.7rem", fontWeight: 800, letterSpacing: "3px", color: "#f0b429" }}>{s.kicker}</span>
        <h2 style={{ fontSize: "2.2rem", fontWeight: 900, color: "#fff", margin: "10px 0 12px 0", lineHeight: 1.1, letterSpacing: "1px" }}>{s.title}</h2>
        <p style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.92rem", margin: "0 0 22px 0", lineHeight: 1.5 }}>{s.sub}</p>
        <button
          onClick={() => onAction(s.action)}
          data-testid="voices-hero-cta"
          style={{
            padding: "13px 28px", borderRadius: "999px", border: "none", cursor: "pointer",
            background: "#fff", color: "#000", fontWeight: 900, fontSize: "0.8rem", letterSpacing: "1.5px"
          }}
        >{s.cta} →</button>
      </div>
      <button onClick={() => setIdx(i => (i - 1 + HERO_SLIDES.length) % HERO_SLIDES.length)} data-testid="voices-hero-prev"
        style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", width: "36px", height: "36px", borderRadius: "50%", border: "1px solid rgba(255,255,255,0.25)", background: "rgba(0,0,0,0.45)", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <ChevronLeft className="w-4 h-4" />
      </button>
      <button onClick={() => setIdx(i => (i + 1) % HERO_SLIDES.length)} data-testid="voices-hero-next"
        style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", width: "36px", height: "36px", borderRadius: "50%", border: "1px solid rgba(255,255,255,0.25)", background: "rgba(0,0,0,0.45)", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <ChevronRight className="w-4 h-4" />
      </button>
      <div style={{ position: "absolute", bottom: "16px", left: "48px", display: "flex", gap: "8px" }}>
        {HERO_SLIDES.map((_, i) => (
          <button key={i} onClick={() => setIdx(i)} data-testid={`voices-hero-dot-${i}`}
            style={{ width: i === idx ? "26px" : "9px", height: "9px", borderRadius: "999px", border: "none", cursor: "pointer", background: i === idx ? "#f0b429" : "rgba(255,255,255,0.35)", transition: "width 0.25s" }} />
        ))}
      </div>
    </div>
  );
}

function FamilyCard({ fam, count, onExplore }) {
  return (
    <div data-testid={`voices-family-${fam.key}`} style={{
      borderRadius: "16px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.1)",
      background: "rgba(255,255,255,0.03)", display: "flex", flexDirection: "column"
    }}>
      <div style={{ height: "170px", background: `url(${fam.img}) center 25%/cover no-repeat`, position: "relative" }}>
        <span style={{
          position: "absolute", top: "12px", left: "12px", padding: "4px 12px", borderRadius: "999px",
          background: "rgba(0,0,0,0.6)", border: `1px solid ${fam.accent}`, color: fam.accent,
          fontSize: "0.62rem", fontWeight: 800, letterSpacing: "1.5px"
        }}>{count} VOICES</span>
      </div>
      <div style={{ padding: "18px 20px", flex: 1, display: "flex", flexDirection: "column" }}>
        <h3 style={{ margin: "0 0 8px 0", color: "#fff", fontSize: "1.05rem", fontWeight: 900, letterSpacing: "2px" }}>{fam.label}</h3>
        <p style={{ margin: "0 0 16px 0", color: "rgba(255,255,255,0.55)", fontSize: "0.8rem", lineHeight: 1.5, flex: 1 }}>{fam.desc}</p>
        <button
          onClick={() => onExplore(fam.key)}
          data-testid={`voices-family-explore-${fam.key}`}
          style={{
            alignSelf: "flex-start", padding: "10px 22px", borderRadius: "999px", cursor: "pointer",
            background: "transparent", border: `1px solid ${fam.accent}`, color: fam.accent,
            fontWeight: 800, fontSize: "0.72rem", letterSpacing: "1.5px"
          }}
        >EXPLORE →</button>
      </div>
    </div>
  );
}

function VoiceCard({ voice, packKey, playingId, loadingId, onPreview, onWorkflow, onCopy, onDownload }) {
  const active = playingId === voice.voice_id;
  const loading = loadingId === voice.voice_id;
  const iconBtn = (onClick, title, testId, children) => (
    <button onClick={onClick} title={title} data-testid={testId} style={{
      width: "34px", height: "34px", borderRadius: "9px", cursor: "pointer", display: "flex",
      alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.06)",
      border: "1px solid rgba(255,255,255,0.14)", color: "rgba(255,255,255,0.75)"
    }}>{children}</button>
  );
  return (
    <div data-testid={`voice-card-${voice.voice_id}`} style={{
      borderRadius: "14px", padding: "16px", background: active ? "rgba(240,180,41,0.08)" : "rgba(255,255,255,0.03)",
      border: active ? "1px solid rgba(240,180,41,0.6)" : "1px solid rgba(255,255,255,0.1)",
      display: "flex", flexDirection: "column", gap: "10px", transition: "border-color 0.2s, background-color 0.2s"
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <button
          onClick={() => onPreview(voice, packKey)}
          data-testid={`voice-preview-${voice.voice_id}`}
          style={{
            width: "44px", height: "44px", borderRadius: "50%", border: "none", cursor: "pointer", flexShrink: 0,
            background: active ? "#f0b429" : "rgba(240,180,41,0.15)", color: active ? "#000" : "#f0b429",
            display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid rgba(240,180,41,0.5)"
          }}
        >
          {loading ? <Loader2 className="w-4 h-4" style={{ animation: "spin 1s linear infinite" }} />
            : active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" style={{ marginLeft: "2px" }} />}
        </button>
        <div style={{ minWidth: 0 }}>
          <div style={{ color: "#fff", fontWeight: 800, fontSize: "0.88rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{voice.name}</div>
          <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.68rem" }}>{voice.language || "English"} • {voice.genre}</div>
        </div>
      </div>
      <p style={{ margin: 0, color: "rgba(255,255,255,0.55)", fontSize: "0.72rem", lineHeight: 1.45, minHeight: "32px" }}>{voice.style}</p>
      <div style={{ display: "flex", gap: "8px" }}>
        {iconBtn(() => onWorkflow(voice, packKey), "Add to Unified Workflow", `voice-workflow-${voice.voice_id}`, <Plus className="w-4 h-4" />)}
        {iconBtn(() => onCopy(voice, packKey), "Copy style prompt", `voice-suno-${voice.voice_id}`, <Copy className="w-4 h-4" />)}
        {iconBtn(() => onDownload(voice, packKey), "Download sample MP3", `voice-download-${voice.voice_id}`, <Download className="w-4 h-4" />)}
      </div>
    </div>
  );
}

export default function VoiceShowcase({ onBack, onAddToWorkflow }) {
  const [catalog, setCatalog] = useState(null);
  const [family, setFamily] = useState("all");
  const [selectedPack, setSelectedPack] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const audioRef = useRef(null);
  const gridRef = useRef(null);
  const detailRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/world-voice-dna`).then(res => setCatalog(res.data)).catch(() => toast.error("Could not load voice catalog"));
    return () => { if (audioRef.current) audioRef.current.pause(); };
  }, []);

  const totalVoices = catalog ? Object.values(catalog).reduce((n, p) => n + (p.voices?.length || 0), 0) : 0;
  const familyCount = (fam) => catalog ? fam.packs.reduce((n, k) => n + (catalog[k]?.voices?.length || 0), 0) : 0;

  const goFamily = (key) => {
    setFamily(key);
    setSelectedPack(null);
    setTimeout(() => gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };

  const openPack = (key) => {
    setSelectedPack(key);
    setTimeout(() => detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };

  const getInstant = useCallback(async (voice, packKey) => {
    const res = await axios.post(`${API}/world-voice-instant`, {
      voice_id: voice.voice_id, voice_name: voice.name, region: packKey, genre: voice.genre
    });
    return res.data;
  }, []);

  const preview = async (voice, packKey) => {
    if (playingId === voice.voice_id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    setLoadingId(voice.voice_id);
    try {
      const data = await getInstant(voice, packKey);
      if (data.status === "ready" && data.audio_url) {
        audioRef.current.src = `${process.env.REACT_APP_BACKEND_URL}${data.audio_url}${data.audio_url.includes("?") ? "&" : "?"}v=mx2`;
        await audioRef.current.play();
        setPlayingId(voice.voice_id);
      } else {
        toast.info("No cached preview for this voice yet — open World Voice DNA in the workflow to generate one (~60s)");
      }
    } catch (e) {
      toast.error("Preview unavailable for this voice");
    } finally {
      setLoadingId(null);
    }
  };

  const copySuno = async (voice, packKey) => {
    let text = `${voice.genre} vocals, ${voice.style}, ${voice.name} style singing, professional studio quality`;
    try {
      const res = await axios.post(`${API}/world-voice-prompt`, {
        voice_name: voice.name, region: packKey, genre: voice.genre, style: voice.style, language: voice.language || "English"
      });
      if (res.data?.suno_prompt) text = res.data.suno_prompt;
    } catch (e) { /* fallback text */ }
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`📋 "${voice.name}" style copied — paste into your AI music platform's style/voice description field`);
    } catch (e) {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      ta.remove();
      if (ok) toast.success(`📋 "${voice.name}" style copied — paste into your AI music platform's style/voice description field`);
      else toast.error("Copy failed - your browser blocked clipboard access");
    }
  };

  const download = async (voice, packKey) => {
    try {
      const data = await getInstant(voice, packKey);
      if (data.status !== "ready" || !data.audio_url) {
        toast.info("No cached sample for this voice yet — open World Voice DNA to generate one (~60s)");
        return;
      }
      const fname = `${voice.name.replace(/[^a-z0-9]+/gi, "_")}_style_sample`;
      const sep = data.audio_url.includes("?") ? "&" : "?";
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.audio_url}${sep}v=mx2&fname=${encodeURIComponent(fname)}`;
      const a = document.createElement("a");
      a.href = url;
      a.download = `${fname}.mp3`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("📥 Style sample downloading — check your Downloads (or the Files app on iPhone)");
    } catch (e) {
      toast.error("Download failed - please try again");
    }
  };

  const addWorkflow = (voice, packKey) => {
    if (onAddToWorkflow) onAddToWorkflow(voice, packKey);
  };

  const packKeys = catalog
    ? Object.keys(catalog).filter(k => family === "all" || (familyOfPack(k)?.key === family))
    : [];
  const packVoices = selectedPack && catalog?.[selectedPack]?.voices ? catalog[selectedPack].voices : [];
  const selectedFam = selectedPack ? familyOfPack(selectedPack) : null;

  return (
    <div data-testid="voice-showcase-view" style={{ maxWidth: "1180px", margin: "0 auto" }}>
      <audio ref={audioRef} preload="metadata" onEnded={() => setPlayingId(null)} onPause={() => setPlayingId(null)} hidden />

      <button
        onClick={onBack}
        data-testid="voices-back-btn"
        style={{
          display: "flex", alignItems: "center", gap: "8px", padding: "12px 20px",
          background: "linear-gradient(135deg, #f0b429, #d97706)", border: "none", borderRadius: "10px",
          color: "#000", fontSize: "0.9rem", fontWeight: 800, cursor: "pointer", marginBottom: "20px"
        }}
      >
        <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
      </button>

      <HeroCarousel onAction={goFamily} />

      <div style={{ display: "flex", alignItems: "baseline", gap: "14px", margin: "0 0 18px 0" }}>
        <h2 style={{ margin: 0, color: "#fff", fontSize: "1.5rem", fontWeight: 900, letterSpacing: "2px" }}>MANNIS VOICES</h2>
        <span style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.8rem", fontWeight: 700 }} data-testid="voices-total-count">
          {totalVoices} voices • {catalog ? Object.keys(catalog).length : 0} collections
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "16px", marginBottom: "34px" }}>
        {FAMILIES.map(f => <FamilyCard key={f.key} fam={f} count={familyCount(f)} onExplore={goFamily} />)}
      </div>

      <div ref={gridRef} style={{ scrollMarginTop: "20px" }}>
        <h2 style={{ margin: "0 0 14px 0", color: "#fff", fontSize: "1.5rem", fontWeight: 900, letterSpacing: "2px" }}>VOICE COLLECTIONS</h2>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "18px" }} data-testid="voices-family-filter">
          {[{ key: "all", label: "ALL", accent: "#f0b429" }, ...FAMILIES].map(f => (
            <button
              key={f.key}
              onClick={() => { setFamily(f.key); setSelectedPack(null); }}
              data-testid={`voices-filter-${f.key}`}
              style={{
                padding: "8px 18px", borderRadius: "999px", cursor: "pointer", fontSize: "0.72rem", fontWeight: 800, letterSpacing: "1px",
                background: family === f.key ? `${f.accent}22` : "rgba(255,255,255,0.04)",
                border: family === f.key ? `1px solid ${f.accent}` : "1px solid rgba(255,255,255,0.12)",
                color: family === f.key ? f.accent : "rgba(255,255,255,0.55)"
              }}
            >{f.label}</button>
          ))}
        </div>

        {!catalog ? (
          <div style={{ display: "flex", alignItems: "center", gap: "10px", color: "#f0b429", padding: "40px 0" }}>
            <Loader2 className="w-5 h-5" style={{ animation: "spin 1s linear infinite" }} />
            <span style={{ fontWeight: 700 }}>Loading voice collections...</span>
          </div>
        ) : (
          <div data-testid="voices-collections-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(215px, 1fr))", gap: "14px", marginBottom: "34px" }}>
            {packKeys.map(key => {
              const pack = catalog[key];
              const fam = familyOfPack(key);
              const open = selectedPack === key;
              return (
                <button
                  key={key}
                  onClick={() => openPack(key)}
                  data-testid={`voice-pack-card-${key}`}
                  style={{
                    textAlign: "left", padding: "18px", borderRadius: "14px", cursor: "pointer",
                    background: open ? `${fam?.accent || "#f0b429"}14` : "rgba(255,255,255,0.03)",
                    border: open ? `1px solid ${fam?.accent || "#f0b429"}` : "1px solid rgba(255,255,255,0.1)",
                    transition: "border-color 0.2s, background-color 0.2s"
                  }}
                >
                  <span style={{
                    display: "inline-block", padding: "3px 10px", borderRadius: "999px", marginBottom: "12px",
                    background: "rgba(0,0,0,0.35)", border: `1px solid ${fam?.accent || "#f0b429"}55`,
                    color: fam?.accent || "#f0b429", fontSize: "0.58rem", fontWeight: 800, letterSpacing: "1.5px"
                  }}>{fam?.label || "VOICES"}</span>
                  <div style={{ fontSize: "1.7rem", marginBottom: "8px" }}>{pack.emoji}</div>
                  <div style={{ color: "#fff", fontWeight: 900, fontSize: "0.95rem", marginBottom: "4px" }}>{pack.name} Voices</div>
                  <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.7rem", marginBottom: "8px" }}>{pack.voices.length} voices</div>
                  <div style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.68rem", lineHeight: 1.4, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                    {pack.voices.slice(0, 3).map(v => v.name).join(" • ")}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {selectedPack && catalog?.[selectedPack] && (
        <div ref={detailRef} data-testid="voice-pack-detail" style={{
          scrollMarginTop: "20px", borderRadius: "16px", padding: "24px",
          background: "rgba(255,255,255,0.02)", border: `1px solid ${selectedFam?.accent || "#f0b429"}55`, marginBottom: "30px"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "14px", marginBottom: "18px", flexWrap: "wrap" }}>
            <span style={{ fontSize: "1.9rem" }}>{catalog[selectedPack].emoji}</span>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: 0, color: "#fff", fontSize: "1.2rem", fontWeight: 900, letterSpacing: "1px" }}>
                {catalog[selectedPack].name} Collection
              </h3>
              <span style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.75rem" }}>
                {packVoices.length} voices — hit ▶ to preview, + to add to your workflow, copy style prompt
              </span>
            </div>
            <button onClick={() => setSelectedPack(null)} data-testid="voice-pack-close"
              style={{ padding: "8px 16px", borderRadius: "999px", cursor: "pointer", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.6)", fontSize: "0.72rem", fontWeight: 700 }}>
              Close
            </button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(255px, 1fr))", gap: "12px" }}>
            {packVoices.map(v => (
              <VoiceCard
                key={`${selectedPack}_${v.voice_id}_${v.name}`}
                voice={v} packKey={selectedPack}
                playingId={playingId} loadingId={loadingId}
                onPreview={preview} onWorkflow={addWorkflow} onCopy={copySuno} onDownload={download}
              />
            ))}
          </div>
        </div>
      )}

      {!selectedPack && catalog && (
        <div style={{
          borderRadius: "14px", padding: "22px", marginBottom: "30px", textAlign: "center",
          background: "rgba(240,180,41,0.05)", border: "1px dashed rgba(240,180,41,0.4)"
        }}>
          <Mic2 className="w-6 h-6" style={{ color: "#f0b429", margin: "0 auto 8px" }} />
          <p style={{ margin: 0, color: "rgba(255,255,255,0.6)", fontSize: "0.85rem" }}>
            Pick a collection above to browse its voices, hear previews and copy style prompts for your AI music platform.
          </p>
        </div>
      )}
    </div>
  );
}
